import React from 'react';
import { createRoot } from 'react-dom/client';
import { HelmetProvider } from '@dr.pogodin/react-helmet';
import { ChakraProvider } from '@chakra-ui/react'
import theme, { toastOptions } from '@/theme/theme';
import App from '@/App';
import { SandboxDataIndicator } from '@/components/feedback/SandboxDataIndicator';
import { VercelAnalytics } from '@/lib/analytics/VercelAnalytics';
import { AppearanceProvider } from '@/lib/appearance/AppearanceProvider';
import { appearanceColorModeManager } from '@/lib/appearance/colorModeManager';
import { installPreloadErrorReload } from '@/utils/preloadErrorReload';
import './index.css';

window.prerenderReady = false;

// Reload once when a lazy chunk fails to load (stale assets after a deploy).
installPreloadErrorReload();

// D45c Phase 3 — single delegated capture-phase `load`/`error` listener
// backing the opacity-only image resolve in src/theme/variables.css. Neither
// event bubbles, so this must run in the capture phase; delegating here
// (rather than a per-component onLoad/onError handler) keeps the listener
// count constant regardless of how many photo containers mount. `error` is
// handled the same as `load` so an image that fails to load still resolves
// to visible instead of staying at opacity:0 forever.
const resolveLazyImage = (event: Event) => {
  const target = event.target;
  if (target instanceof HTMLImageElement && target.getAttribute('loading') === 'lazy') {
    target.classList.add('img-loaded');
  }
};
document.addEventListener('load', resolveLazyImage, true);
document.addEventListener('error', resolveLazyImage, true);

const container = document.getElementById('root');
const root = createRoot(container!);
root.render(
  <React.StrictMode>
    <HelmetProvider>
      <ChakraProvider theme={theme} colorModeManager={appearanceColorModeManager} toastOptions={toastOptions}>
        <AppearanceProvider>
          {/* D70: staging-only overlay, first in DOM order, outside the router and every layout. */}
          <SandboxDataIndicator />
          <App />
          <VercelAnalytics />
        </AppearanceProvider>
      </ChakraProvider>
    </HelmetProvider>
  </React.StrictMode>
);
